import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { getCurrentProject, getCurrentUserAssociation } from '../modules/project';
import { useUser } from './UserContext';
import {
  buildPermissionSet,
  checkPermission,
  checkAnyPermission,
  checkAllPermissions,
} from '../lib/permissions';

const ProjectContext = createContext(null);

const normalizePermissions = (permissions = []) =>
  [...new Set(permissions.filter(Boolean))];

export const ProjectProvider = ({ children }) => {
  const { projectId: routeParamId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const { role: orgRole, userPermissions: orgPermissions } = useUser();

  const hasOrgPermissions =
    Array.isArray(orgPermissions) && orgPermissions.length > 0;

  const [currentProject, setCurrentProject] = useState(null);
  const [projectUser, setProjectUser] = useState(null);
  const [projectRole, setProjectRole] = useState(null);
  const [projectPermissions, setProjectPermissions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState(null);

  const projectId = useMemo(() => {
    if (routeParamId) return routeParamId;
    const match = location.pathname.match(/\/project\/([^/]+)/);
    return match ? match[1] : null;
  }, [routeParamId, location.pathname]);

  const resetProjectState = useCallback(() => {
    setCurrentProject(null);
    setProjectUser(null);
    setProjectRole(null);
    setProjectPermissions([]);
  }, []);

  const refreshProject = useCallback(async () => {
    if (!projectId) {
      resetProjectState();
      setIsReady(true);
      return null;
    }

    setIsLoading(true);
    setError(null);

    try {
      const [association, project] = await Promise.all([
        getCurrentUserAssociation(projectId),
        getCurrentProject(projectId),
      ]);

      setProjectUser(association);
      if (association?.permissions?.length > 0) {
        setProjectRole(association.role ?? null);
        setProjectPermissions(normalizePermissions(association.permissions ?? []));
      } else if (hasOrgPermissions) {
        setProjectRole(orgRole);
        setProjectPermissions(normalizePermissions(orgPermissions));
      } else if (association) {
        setProjectRole(association.role ?? null);
        setProjectPermissions([]);
      } else {
        setProjectRole(null);
        setProjectPermissions([]);
      }

      setCurrentProject(project);
      return { association, project };
    } catch (err) {
      resetProjectState();
      setError(err);
      navigate('/access-denied', { replace: true });
    } finally {
      setIsLoading(false);
      setIsReady(true);
    }
  }, [projectId, resetProjectState, orgRole, orgPermissions, hasOrgPermissions]);

  useEffect(() => {
    setIsReady(false);
    refreshProject().catch(console.error);
  }, [refreshProject]);

  const permissionSet = useMemo(
    () => buildPermissionSet(projectPermissions),
    [projectPermissions]
  );

  const can = useCallback(
    (permission) => checkPermission(permissionSet, permission),
    [permissionSet]
  );

  const hasAny = useCallback(
    (permissions) => checkAnyPermission(permissionSet, permissions),
    [permissionSet]
  );

  const hasAll = useCallback(
    (permissions) => checkAllPermissions(permissionSet, permissions),
    [permissionSet]
  );

  const value = useMemo(() => ({
    projectId: projectId ?? null,
    currentProject,
    projectUser,
    projectRole,
    projectPermissions,
    permissionSet,
    can,
    hasAny,
    hasAll,
    isLoading,
    isReady,
    error,
    refreshProject,
    refreshProjectUser: refreshProject,
  }), [
    projectId, currentProject, projectUser, projectRole, projectPermissions,
    permissionSet, can, hasAny, hasAll, isLoading, isReady, error, refreshProject,
  ]);

  return (
    <ProjectContext.Provider value={value}>
      {children}
    </ProjectContext.Provider>
  );
};

export const useProject = () => {
  const context = useContext(ProjectContext);
  if (!context) throw new Error('useProject must be used within a ProjectProvider');
  return context;
};

export const useProjectOptional = () => useContext(ProjectContext);